import { useEffect, useMemo, useRef } from 'react';

import Icon, { ICON_PLAY } from '~/components/icon';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { eCommerceCampaignClick, eCommerceCampaignImp } from '~/components/promotions/ducks';
import { TranslationHash } from '~/components/translation/use-translations';
import LazyLoadImage from '~/components/utilities/lazyload-image';
import { TimingObject } from '~/utils/count-down';

import { useCasinoSharedSelector } from '../ducks/types';
import GameNameInfiniteScrolling from './game-name';
import { getForegroundImageSrcSet, WidgetData } from './utils';
import {
	BackGroundImage,
	CardWrapperCountdown,
	CenteredContainer,
	ComingSoonCountdown,
	CountDownWrapper,
	ForegroundImage,
	MiddleWrapper,
	PlayButton,
	Time,
	TimeAbbreviation,
	TimeWrapper,
	TitleWrapper
} from './widget-styles';
import useConfiguration from '~/configuration/use-configuration';

type Props = {
	gameName: string;
	timingObject: TimingObject;
	toggleShowModal: () => void;
	translations: TranslationHash;
} & WidgetData;

const HorizontalCardCountdown = ({
	gameName,
	backgroundImage,
	foregroundImage,
	timingObject,
	toggleShowModal,
	comingSoonText,
	translations,
	id
}: Props) => {
	const configuration = useConfiguration();
	const foregroundImageSrcSet = useMemo(() => getForegroundImageSrcSet(foregroundImage, 128), [foregroundImage]);
	const smaller = useCasinoSharedSelector(isSmallerSelector);

	const containerRef = useRef<HTMLDivElement>(null);

	const openModal = () => {
		eCommerceCampaignClick(gameName, id, 0);
		toggleShowModal();
	};

	useEffect(() => {
		eCommerceCampaignImp([
			{
				id,
				name: gameName
			}
		]);
	}, [gameName, id]);

	return (
		<BackGroundImage
			backgroundImage={backgroundImage}
			onClick={openModal}
			tmpGameIconSrc={configuration?.casino?.tmpGameIcon?.src}
		>
			<CardWrapperCountdown>
				<ForegroundImage>
					<LazyLoadImage
						src={foregroundImage}
						srcSet={foregroundImageSrcSet}
						sizes="128px"
						alt={`${gameName} image`}
						lazyload
					/>
				</ForegroundImage>
				<MiddleWrapper>
					<CenteredContainer ref={containerRef}>
						<GameNameInfiniteScrolling gameName={gameName} containerRef={containerRef} />
						<TitleWrapper>
							<ComingSoonCountdown>{comingSoonText}</ComingSoonCountdown>
						</TitleWrapper>
					</CenteredContainer>
					<CountDownWrapper>
						{!!timingObject.days && (
							<TimeWrapper>
								<Time>{String(timingObject.days).padStart(2, '0')}</Time>
								<TimeAbbreviation>{translations.WIDGET_DAYS_ABBREVIATION}</TimeAbbreviation>
							</TimeWrapper>
						)}
						<TimeWrapper>
							<Time>{String(timingObject.hours).padStart(2, '0')}</Time>
							<TimeAbbreviation>{translations.WIDGET_HOURS_ABBREVIATION}</TimeAbbreviation>
						</TimeWrapper>
						<TimeWrapper>
							<Time>{String(timingObject.minutes).padStart(2, '0')}</Time>
							<TimeAbbreviation>{translations.WIDGET_MINUTES_ABBREVIATION}</TimeAbbreviation>
						</TimeWrapper>
						{!timingObject.days && (
							<TimeWrapper>
								<Time>{String(timingObject.seconds).padStart(2, '0')}</Time>
								<TimeAbbreviation>{translations.WIDGET_SECONDS_ABBREVIATION}</TimeAbbreviation>
							</TimeWrapper>
						)}
					</CountDownWrapper>
				</MiddleWrapper>
				<PlayButton>
					<Icon width={smaller ? 48 : 72} height={smaller ? 48 : 72} type={ICON_PLAY as string} />
				</PlayButton>
			</CardWrapperCountdown>
		</BackGroundImage>
	);
};

export default HorizontalCardCountdown;
